<script setup>
import { defineEmits, computed, h, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import CampaignExportReportMessagesModal from './CampaignExportReportMessagesModal.vue';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  campaignReport: {
    type: Object,
    default: () => ({}),
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'exportReportMessages',
  'cleanFilter',
  'searchByContactinfo',
  'pageChange',
  // 'statusChange',
]);

const { t } = useI18n();

const contactInfoQuery = ref('');
const currentSearchedContactInfoQuery = ref('');
const currentSearchedStatus = ref('');
const showExportReportModal = ref(false);

const tableData = computed(
  () =>
    props.campaignReport?.messages?.map(response => ({
      contact: response.contact,
      conversation: response.conversation,
      protocol: response.protocol,
      status: response.campaign_message_status,
      id: response.id,
    })) || []
);

const totalResponseCount = computed(
  () => props.campaignReport?.messages_count || 0
);

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('contact', {
    header: t('CAMPAIGN.REPORT.MESSAGES_TABLE.RECIPIENT'),
    width: 200,
    cell: cellProps => {
      const contact = cellProps.row.original.contact;
      return contact
        ? h(UserAvatarWithName, {
            user: contact,
            size: '24px',
            textClass: 'text-sm text-n-slate-12 max-w-[200px]',
          })
        : '---';
    },
  }),
  columnHelper.accessor('status', {
    header: t('CAMPAIGN.REPORT.MESSAGES_TABLE.STATUS'),
    width: 120,
    align: 'center',
    cell: cellProps => {
      const status = cellProps.row.original.status;
      return h(
        'span',
        t(`CAMPAIGN.REPORT.STATUS.${status.toUpperCase()}`) || '---'
      );
    },
  }),
  columnHelper.accessor('protocol', {
    header: t('CONVERSATION.HEADER.PROTOCOL_LABEL'),
    width: 200,
    align: 'center',
    cell: cellProps => h('span', cellProps.getValue() || '---'),
  }),
  columnHelper.accessor('conversationLink', {
    header: t('CAMPAIGN.REPORT.MESSAGES_TABLE.ACTIONS'),
    width: 100,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(
        RouterLink,
        {
          to: frontendURL(
            conversationUrl({
              accountId: row.conversation.account_id,
              id: row.conversation.id,
              messageId: row.id,
            })
          ),
          class: 'text-blue-800 hover:underline',
        },
        () => t('CAMPAIGN.REPORT.MESSAGES_TABLE.ACCESS_CHAT')
      );
    },
  }),
];

const paginationParams = computed(() => ({
  pageIndex: props.pageIndex - 1,
  pageSize: 10,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  manualPagination: true,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalResponseCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPage = updater(paginationParams.value).pageIndex + 1;
    emit('pageChange', newPage);
  },
});

const searchByContactInfo = () => {
  currentSearchedContactInfoQuery.value = contactInfoQuery.value;
  emit('searchByContactinfo', contactInfoQuery.value);
};

const cleanFilter = () => {
  contactInfoQuery.value = '';
  currentSearchedStatus.value = '';
  currentSearchedContactInfoQuery.value = '';
  emit('cleanFilter');
};

const toogleExportReportModal = () => {
  showExportReportModal.value = !showExportReportModal.value;
};

const exportReportMessages = payload => {
  emit('exportReportMessages', payload);
};
</script>

<template>
  <div class="table-container">
    <div class="flex justify-between items-center">
      <div>
        <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('CAMPAIGN.REPORT.MESSAGES_TABLE.HEADER') }}
        </h3>
        <h5 class="text-sm text-slate-800 dark:text-slate-100">
          {{ $t('CAMPAIGN.REPORT.MESSAGES_TABLE.DESC') }}
        </h5>
      </div>
      <woot-button
        color-scheme="info"
        icon="download"
        class="clear"
        @click="toogleExportReportModal"
      >
        {{ t('CAMPAIGN.REPORT.MESSAGES_TABLE.EXPORT') }}
      </woot-button>
    </div>

    <!-- search input -->
    <div class="flex mt-4 mb-2">
      <woot-input
        v-model="contactInfoQuery"
        class="min-w-[18rem] max-w-[20rem]"
        type="text"
        :placeholder="$t('CAMPAIGN.REPORT.MESSAGES_TABLE.NAME_OR_NUMBER')"
      />
      <woot-button
        :disabled="isLoading"
        :is-loading="isLoading"
        color-scheme="primary"
        @click.prevent="searchByContactInfo"
      >
        <fluent-icon icon="search" class="search-icon" />
      </woot-button>
      <woot-button
        v-if="currentSearchedStatus || currentSearchedContactInfoQuery"
        color-scheme="alert"
        @click="cleanFilter"
      >
        {{ $t('FILTER.CLEAR_BUTTON_LABEL') }}
      </woot-button>
    </div>

    <div class="shadow rounded-xl bg-white dark:bg-slate-900 p-4">
      <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />

      <div
        v-show="!tableData.length"
        class="h-48 flex items-center justify-center text-slate-600 text-sm"
      >
        {{ $t('CAMPAIGN.REPORT.MESSAGES_TABLE.NO_RECORDS') }}
      </div>

      <div v-if="totalResponseCount" class="table-pagination mt-4">
        <Pagination :table="table" />
      </div>
    </div>

    <woot-modal
      v-if="showExportReportModal"
      v-model:show="showExportReportModal"
      :on-close="() => (showExportReportModal = false)"
    >
      <CampaignExportReportMessagesModal
        @close-export-report-modal="() => (showExportReportModal = false)"
        @export-report-messages="exportReportMessages"
      />
    </woot-modal>
  </div>
</template>

<style lang="scss" scoped>
.table-container {
  display: flex;
  flex-direction: column;
  flex: 1;
}
</style>
